/**
 * PHASE 6: Gate 6A Resume Modal
 * 
 * Modal for resuming partial TTS processing.
 * Processes ONLY missing segments using existing TTS worker.
 * 
 * Used when:
 * - User clicked "Close with Partial" on stalled Gate 6
 * - Later clicks "Finish Audio Processing" on Gate 7 (EditTitlePage)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, CheckCircle, XCircle, RefreshCw, AlertTriangle } from 'lucide-react';

const StreamlinedTTSResumeModal = ({ 
  isOpen,
  jobId, 
  authorId,
  remainingCount,
  totalCount,
  onComplete, 
  onClose 
}) => {
  const [status, setStatus] = useState('starting'); // starting, in_progress, complete, failed, error
  const [progress, setProgress] = useState({
    processedCount: totalCount - remainingCount,
    totalCount: totalCount,
    percentComplete: ((totalCount - remainingCount) / totalCount) * 100
  });
  const [error, setError] = useState(null);
  const [currentSegment, setCurrentSegment] = useState(null);
  const pollIntervalRef = useRef(null);

  // Start resume when modal opens
  useEffect(() => {
    if (isOpen && jobId) {
      startResume();
    }
    
    return () => {
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, jobId]);

  const getApiBase = () => {
    if (authorId) {
      return `/api/publisher/managed-authors/${authorId}/simple-ingest`;
    }
    return '/api/simple-ingest';
  };

  const startResume = async () => {
    try {
      console.log('[Gate6A] Starting resume for', remainingCount, 'segments');
      setStatus('starting');
      setError(null);
      
      // Get auth token - try multiple keys for compatibility
      const authToken = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('auth_token');
      
      if (!authToken) {
        console.error('[Gate6A] No auth token found');
        throw new Error('Authentication required. Please log in again.');
      }
      
      const response = await fetch(
        `${getApiBase()}/jobs/${jobId}/gate-6a/resume-partial`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Gate6A] Resume failed:', response.status, errorData);
        throw new Error(errorData.message || `Failed to start resume (${response.status})`);
      }

      const result = await response.json();
      console.log('[Gate6A] Resume response:', result);

      if (result.data?.status === 'complete') {
        // Already complete
        setStatus('complete');
        setProgress({
          processedCount: result.data.totalSegments,
          totalCount: result.data.totalSegments,
          percentComplete: 100
        });
        // Delay callback to show 100%
        setTimeout(() => {
          onComplete && onComplete();
        }, 1500);
      } else {
        // Start polling
        setStatus('in_progress');
        startPolling();
      }

    } catch (err) {
      console.error('[Gate6A] Resume error:', err);
      setError(err.message);
      setStatus('error');
    }
  };

  const startPolling = () => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Get auth token once for polling
    const authToken = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('auth_token');

    // Poll every 3 seconds
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(
          `${getApiBase()}/jobs/${jobId}/gate-6a/progress`,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          }
        );

        if (!response.ok) {
          console.error('[Gate6A] Progress check failed:', response.status);
          throw new Error('Progress check failed');
        }

        const result = await response.json();
        const progressData = result.data;
        
        console.log('[Gate6A] Progress:', progressData);

        setProgress({
          processedCount: progressData.processedCount,
          totalCount: progressData.totalCount,
          percentComplete: progressData.percentComplete
        });
        setCurrentSegment(progressData.currentSegment);

        if (progressData.status === 'complete') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('complete');
          console.log('[Gate6A] Processing complete!');
          
          // Give UI a moment to show 100% before callback
          setTimeout(() => {
            onComplete && onComplete();
          }, 1500);
        } else if (progressData.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStatus('failed');
          setError('Processing failed');
        }

      } catch (err) {
        console.error('[Gate6A] Polling error:', err);
        // Don't stop polling on transient errors
      }
    }, 3000);
  };

  const handleRetry = () => {
    startResume();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <RefreshCw className={`w-5 h-5 ${status === 'in_progress' ? 'animate-spin text-blue-400' : 'text-amber-400'}`} />
            Finishing Audio Processing
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Error State */}
          {status === 'error' && (
            <div className="text-center p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-300 font-semibold mb-2">Error Starting Resume</p>
              <p className="text-slate-400 text-sm mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={handleRetry}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="border-slate-600"
                >
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* Starting State */}
          {status === 'starting' && (
            <div className="text-center p-4">
              <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-3 animate-spin" />
              <p className="text-slate-300">Starting audio processing...</p>
              <p className="text-slate-500 text-sm mt-2">
                Processing {remainingCount} remaining segment{remainingCount !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* In Progress State */}
          {status === 'in_progress' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-slate-300 mb-1">
                  Processing remaining segments...
                </p>
                <p className="text-2xl font-bold text-blue-400">
                  {progress.processedCount} / {progress.totalCount}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 h-full rounded-full transition-all duration-500"
                  style={{ width: `${progress.percentComplete}%` }}
                />
              </div>

              <p className="text-center text-slate-400 text-sm">
                {Math.round(progress.percentComplete)}% complete
              </p>

              {/* Current Segment */}
              {currentSegment && (
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                    <span className="text-slate-400 text-sm">Currently processing:</span>
                  </div>
                  <p className="text-slate-300 text-sm mt-1 font-mono">{currentSegment}</p>
                </div>
              )}

              <p className="text-xs text-slate-500 text-center">
                Please keep this window open until processing completes
              </p>
            </div>
          )}

          {/* Complete State */}
          {status === 'complete' && (
            <div className="text-center p-4 bg-emerald-900/30 border border-emerald-500/50 rounded-lg">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-300 font-semibold text-lg mb-2">
                Audio Processing Complete!
              </p>
              <p className="text-slate-400 text-sm">
                All {progress.totalCount} segments now have audio.
              </p>
              <p className="text-slate-500 text-xs mt-3">
                The page will refresh momentarily...
              </p>
            </div>
          )}

          {/* Failed State */}
          {status === 'failed' && (
            <div className="text-center p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-300 font-semibold mb-2">Processing Failed</p>
              <p className="text-slate-400 text-sm mb-4">
                {error || 'Some segments could not be processed'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={handleRetry}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  Retry
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="border-slate-600"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StreamlinedTTSResumeModal;
